import {
  ChakraBox,
  ChakraFlex,
  ChakraGrid,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  ToastMessage,
  UniversalSelect,
} from '@equip.health/ui';
import { useEffect, useState } from 'react';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  CONFIRM_DELETION_BUTTON_TEXT,
  PHI_EXPOSURE_WARNING_MESSAGE,
  VOID_DOCUMENT_BUTTON_TEXT,
  VOID_REASON_WRONG_PATIENT_CHART,
} from '~/lib/constants/documentCenter.constants';
import { GENERIC_API_FAILURE_MESSAGE } from '~/lib/constants/profile.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGetPatientDocumentVoidReasonsQuery } from '~/store/services/enumerations';

interface VoidDocumentModalProps {
  document: PatientDocumentV2 | null;
  id: string;
  isErrorToastVisible?: boolean;
  isLoading: boolean;
  isOpen: boolean;
  onDeleteConfirm: (reasonId: string) => void;
  onDeleteClose: () => void;
}

const VoidDocumentModal = ({
  id,
  document,
  isOpen,
  isLoading,
  isErrorToastVisible,
  onDeleteClose,
  onDeleteConfirm,
}: VoidDocumentModalProps) => {
  const { trackEvent } = useAnalytics();

  const [deleteReason, setDeleteReason] = useState<string | null>(null);

  const {
    data: voidReasons = [],
    isLoading: isGetPatientDocumentVoidReasonsLoading,
  } = useGetPatientDocumentVoidReasonsQuery(undefined, {
    skip: !isOpen,
  });

  const fileName = document?.fileName ?? 'This document';

  const selectedOption =
    voidReasons.find((opt) => opt.value === deleteReason) ?? null;

  useEffect(() => {
    if (isOpen) {
      setDeleteReason(null);
    }
  }, [isOpen, document?.documentExternalId]);

  const handleDeleteConfirm = () => {
  if (isWrongPatient) {
    trackEvent(DocumentCenterEvents.voidDocumentModalConfirmDeletionClicked, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
      DocumentExternalId: document?.documentExternalId,
      FileName: fileName,
    });
  } else {
    trackEvent(DocumentCenterEvents.voidDocumentModalConfirmClicked, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
      DocumentExternalId: document?.documentExternalId,
      FileName: fileName,
    });
  }
    if (deleteReason) {
      onDeleteConfirm(deleteReason);
    }
  };

  const isWrongPatient = deleteReason === VOID_REASON_WRONG_PATIENT_CHART;

  return (
    <ModalV2
      footerContent={
        <ChakraFlex justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${id}__cancel`}
            leftIcon={FAIconName.XmarkRegular}
            onClick={() => {
              trackEvent(DocumentCenterEvents.voidDocumentModalCancelClicked, {
                Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                DocumentExternalId: document?.documentExternalId,
                FileName: fileName,
              });
              onDeleteClose();
            }}
            variant="secondary"
          >
            Cancel
          </FireflyButton>

          <FireflyButton
            id={`${id}__confirm-void`}
            isDisabled={!deleteReason || isLoading}
            isLoading={isLoading}
            leftIcon={FAIconName.trashRegular}
            onClick={handleDeleteConfirm}
            variant="accent.critical"
          >
            {isWrongPatient
              ? CONFIRM_DELETION_BUTTON_TEXT
              : VOID_DOCUMENT_BUTTON_TEXT}
          </FireflyButton>
        </ChakraFlex>
      }
      id={`${id}__void-document-modal`}
      isOpen={isOpen}
      onClose={() => {
        trackEvent(DocumentCenterEvents.voidDocumentModalCloseIconClicked, {
          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
        });
        onDeleteClose();
      }}
      title="Void document?"
    >
      <ChakraGrid gap="24px">
        <Text>
          {`${fileName} will no longer be accessible from this
          patient's chart. This cannot be undone.`}
        </Text>

        <ChakraBox>
          <InputGroupV2
            id={`${id}__void-reason`}
            isRequired
            label="Reason for voiding"
            shouldShowAsteriskWhenRequired
          >
            <UniversalSelect
              id={`${id}__void-reason`}
              isLoading={isGetPatientDocumentVoidReasonsLoading}
              isSingleSelect
              modalId={`${id}__void-reason-modal`}
              onMenuOpen={() => {
                trackEvent(DocumentCenterEvents.voidReasonMenuOpened, {
                  Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                  DocumentExternalId: document?.documentExternalId,
                  FileName: fileName,
                });
              }}
              onChange={(option) => {
                setDeleteReason(option?.value ?? null);
                if (
                  option?.value &&
                  option?.value !== VOID_REASON_WRONG_PATIENT_CHART
                ) {
                  trackEvent(DocumentCenterEvents.voidReasonMenuOptionClicked, {
                    Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                    DocumentExternalId: document?.documentExternalId,
                    FileName: fileName,
                    Reason: option?.label,
                    ReasonValue: option?.value,
                  });
                } else if (option?.value === VOID_REASON_WRONG_PATIENT_CHART) {
                  trackEvent(
                    DocumentCenterEvents.voidReasonWrongPatientChartSelected,
                    {
                      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                      DocumentExternalId: document?.documentExternalId,
                      FileName: fileName,
                    },
                  );
                }
              }}
              options={voidReasons}
              placeholder="Select a reason"
              value={selectedOption}
            />
          </InputGroupV2>
        </ChakraBox>
        {isWrongPatient && (
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id={`${id}__void-reason-warning`}
            text={PHI_EXPOSURE_WARNING_MESSAGE}
            variant="warning"
          />
        )}
        {isErrorToastVisible && (
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id={`${id}__warning`}
            isFullWidth
            text={GENERIC_API_FAILURE_MESSAGE}
            variant="critical"
          />
        )}
      </ChakraGrid>
    </ModalV2>
  );
};

export default VoidDocumentModal;
